import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { Shell } from "@/components/Shell";
import { Search, Ban, CheckCircle2, Eye } from "lucide-react";

const euro = (n) => `${(Number(n) || 0).toLocaleString("fr-FR")} €`;

export default function AdminAmbassadors() {
  const navigate = useNavigate();
  const [ambassadors, setAmbassadors] = useState([]);
  const [q, setQ] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/admin/ambassadors");
      setAmbassadors(data);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || e.message);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const toggleSuspend = async (amb, e) => {
    e.stopPropagation();
    try {
      await api.patch(`/admin/ambassadors/${amb.id}`, { suspended: !amb.suspended });
      toast.success(amb.suspended ? "Ambassadeur réactivé." : "Ambassadeur suspendu.");
      load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || err.message);
    }
  };

  const filtered = ambassadors.filter((a) => {
    const s = q.trim().toLowerCase();
    if (!s) return true;
    return (
      `${a.first_name} ${a.last_name}`.toLowerCase().includes(s) ||
      (a.code || "").toLowerCase().includes(s) ||
      (a.email || "").toLowerCase().includes(s)
    );
  });

  return (
    <Shell role="admin">
      <div className="px-5 py-8 sm:px-8">
        <h1 className="text-3xl font-extrabold tracking-tighter">Ambassadeurs</h1>
        <p className="mt-2 text-neutral-500">
          Programme de parrainage : commission FIXE versée après inscription validée —
          <strong> 25 € par filleul (particulier)</strong>, <strong>50 € (professionnel)</strong>.
          Plafond de 10 filleuls rémunérés / mois pour les particuliers.
        </p>

        <div className="mt-6 flex items-center gap-2 rounded-lg border border-neutral-200 bg-white px-3 py-2 sm:max-w-sm">
          <Search className="h-4 w-4 text-neutral-400" />
          <input
            data-testid="amb-search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Rechercher (nom, code, email)…"
            className="w-full text-sm outline-none"
          />
        </div>

        <div className="mt-6 overflow-x-auto rounded-lg border border-neutral-200 bg-white">
          <table className="w-full text-sm" data-testid="admin-ambassadors-table">
            <thead className="bg-neutral-50 text-left text-[11px] uppercase tracking-wider text-neutral-500">
              <tr>
                <th className="px-4 py-3">Ambassadeur</th>
                <th className="px-4 py-3">Code</th>
                <th className="px-4 py-3">Type</th>
                <th className="px-4 py-3">Candidats</th>
                <th className="px-4 py-3">Validées</th>
                <th className="px-4 py-3">Commission due</th>
                <th className="px-4 py-3">Payé</th>
                <th className="px-4 py-3">Statut</th>
                <th className="px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={9} className="px-4 py-10 text-center text-neutral-400">
                    Aucun ambassadeur pour le moment.
                  </td>
                </tr>
              ) : (
                filtered.map((a) => (
                  <tr
                    key={a.id}
                    onClick={() => navigate(`/admin/ambassadeurs/${a.id}`)}
                    data-testid={`amb-row-${a.id}`}
                    className="cursor-pointer hover:bg-neutral-50"
                  >
                    <td className="px-4 py-3">
                      <div className="font-semibold text-neutral-800">{a.first_name} {a.last_name}</div>
                      <div className="text-xs text-neutral-400">{a.email}</div>
                    </td>
                    <td className="px-4 py-3 font-mono font-semibold text-brand">{a.code}</td>
                    <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                      <select
                        value={a.type || "particulier"}
                        data-testid={`amb-type-select-${a.id}`}
                        onChange={async (e) => {
                          try {
                            await api.patch(`/admin/ambassadors/${a.id}`, { type: e.target.value });
                            toast.success("Type mis à jour.");
                            load();
                          } catch (err) {
                            toast.error(apiError(err.response?.data?.detail) || err.message);
                          }
                        }}
                        className="rounded-md border border-neutral-300 bg-white px-2 py-1 text-xs font-semibold outline-none focus:border-brand"
                      >
                        <option value="particulier">Particulier · 25 €</option>
                        <option value="professionnel">Professionnel · 50 €</option>
                      </select>
                    </td>
                    <td className="px-4 py-3">{a.candidates_total}</td>
                    <td className="px-4 py-3">{a.validated_inscriptions}</td>
                    <td className="px-4 py-3 font-semibold">{euro(a.commission_owed)}</td>
                    <td className="px-4 py-3 text-neutral-500">{euro(a.commission_paid)}</td>
                    <td className="px-4 py-3">
                      {a.suspended ? (
                        <span className="rounded-full bg-red-50 px-2.5 py-1 text-xs font-semibold text-red-700">Suspendu</span>
                      ) : (
                        <span className="rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700">Actif</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => navigate(`/admin/ambassadeurs/${a.id}`)}
                          className="rounded-md p-1.5 text-neutral-500 hover:bg-neutral-100 hover:text-brand"
                          title="Voir"
                          data-testid={`amb-view-${a.id}`}
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                        <button
                          onClick={(e) => toggleSuspend(a, e)}
                          className={`rounded-md p-1.5 hover:bg-neutral-100 ${a.suspended ? "text-emerald-600" : "text-red-500"}`}
                          title={a.suspended ? "Réactiver" : "Suspendre"}
                          data-testid={`amb-suspend-${a.id}`}
                        >
                          {a.suspended ? <CheckCircle2 className="h-4 w-4" /> : <Ban className="h-4 w-4" />}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </Shell>
  );
}
